import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import { DOCUMENT, documentFormLabelSx } from '../constants/documentTheme';
import { partnerLinkSx, partnerOutlineBtnSx, partnerOxbloodBtnSx } from './PartnerListChrome';

const emptyForm = {
  company_name: '',
  contact_name: '',
  email: '',
  phone: '',
  notes: '',
};

const paperSx = {
  borderRadius: 0,
  border: `1px solid ${DOCUMENT.rule}`,
  bgcolor: DOCUMENT.canvas,
  color: DOCUMENT.ink,
  fontFamily: DOCUMENT.sans,
  boxShadow: 'none',
  '& .MuiAutocomplete-option': {
    fontFamily: DOCUMENT.sans,
    fontSize: 14,
  },
  '& .MuiAutocomplete-option.Mui-focused, & .MuiAutocomplete-option[aria-selected="true"]': {
    bgcolor: DOCUMENT.ivory,
    color: DOCUMENT.oxblood,
  },
};

/**
 * Partner DMC contact book picker. Selecting a DMC forces request-to-book.
 */
const PartnerDmcField = ({ dmcId, onChange }) => {
  const { t } = useTranslation();
  const [dmcs, setDmcs] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    let cancelled = false;
    Promise.resolve(api.get('/partner-packages/dmcs', { usePartnerAuth: true }))
      .then((response) => {
        if (!cancelled) setDmcs(response?.data?.dmcs || []);
      })
      .catch(() => {
        if (!cancelled) setDmcs([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const selected = useMemo(
    () => dmcs.find((dmc) => Number(dmc.id) === Number(dmcId)) || null,
    [dmcs, dmcId]
  );

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setFormError('');
    setDialogOpen(true);
  };

  const openEdit = () => {
    if (!selected) return;
    setEditingId(selected.id);
    setForm({
      company_name: selected.company_name || '',
      contact_name: selected.contact_name || '',
      email: selected.email || '',
      phone: selected.phone || '',
      notes: selected.notes || '',
    });
    setFormError('');
    setDialogOpen(true);
  };

  const saveDmc = async () => {
    if (!form.company_name.trim() || (!form.email.trim() && !form.phone.trim())) {
      setFormError(t('createPackage.dmcContactRequired'));
      return;
    }
    try {
      setSaving(true);
      setFormError('');
      const response = editingId
        ? await api.put(`/partner-packages/dmcs/${editingId}`, form, { usePartnerAuth: true })
        : await api.post('/partner-packages/dmcs', form, { usePartnerAuth: true });
      const saved = response.data?.dmc;
      if (saved) {
        setDmcs((prev) => {
          const rest = prev.filter((dmc) => dmc.id !== saved.id);
          return [...rest, saved].sort((a, b) => a.company_name.localeCompare(b.company_name));
        });
        onChange(saved.id, { bookingMethod: 'inquiry' });
      }
      setDialogOpen(false);
    } catch (err) {
      setFormError(err.response?.data?.error || t('createPackage.dmcSaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ mt: 3 }} data-testid="partner-dmc-field">
      <Typography sx={{ ...documentFormLabelSx, mb: 1 }}>{t('createPackage.fulfilledBy')}</Typography>
      <Typography sx={{ fontSize: 12.5, color: DOCUMENT.ink3, mb: 1.25, maxWidth: 560 }}>
        {t('createPackage.fulfilledByHint')}
      </Typography>
      <Autocomplete
        options={dmcs}
        value={selected}
        openOnFocus
        onChange={(_event, next) => {
          onChange(next ? next.id : null, next ? { bookingMethod: 'inquiry' } : {});
        }}
        getOptionLabel={(option) => option.company_name || ''}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        noOptionsText={t('createPackage.dmcNone')}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="standard"
            placeholder={t('createPackage.dmcPlaceholder')}
            inputProps={{
              ...params.inputProps,
              'data-testid': 'partner-dmc-input',
            }}
          />
        )}
        PaperComponent={({ children, ...paperProps }) => (
          <Paper {...paperProps} sx={paperSx}>
            {children}
            <Box
              component="button"
              type="button"
              data-testid="partner-dmc-add"
              onMouseDown={(event) => event.preventDefault()}
              onClick={openCreate}
              sx={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                border: 0,
                borderTop: `1px solid ${DOCUMENT.rule}`,
                bgcolor: DOCUMENT.canvas,
                color: DOCUMENT.oxblood,
                fontFamily: DOCUMENT.sans,
                fontSize: 12,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                px: 2,
                py: 1.25,
                cursor: 'pointer',
                '&:hover': { bgcolor: DOCUMENT.ivory },
              }}
            >
              {t('createPackage.addDmc')}
            </Box>
          </Paper>
        )}
        ListboxProps={{ sx: { maxHeight: 240, fontFamily: DOCUMENT.sans, py: 0 } }}
        sx={{ maxWidth: 480 }}
      />
      {selected ? (
        <Button type="button" onClick={openEdit} sx={{ ...partnerLinkSx, mt: 1.25 }}>
          {t('createPackage.editDmc')}
        </Button>
      ) : null}
      {selected ? (
        <Typography sx={{ fontSize: 13, color: DOCUMENT.ink2, mt: 1 }} data-testid="partner-dmc-selected">
          {[selected.company_name, selected.contact_name, selected.email, selected.phone].filter(Boolean).join(' · ')}
        </Typography>
      ) : null}

      <Dialog
        open={dialogOpen}
        onClose={() => !saving && setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 0,
            bgcolor: DOCUMENT.canvas,
            border: `1px solid ${DOCUMENT.rule}`,
            boxShadow: 'none',
          },
        }}
      >
        <DialogTitle sx={{ fontFamily: DOCUMENT.display, fontWeight: 400 }}>
          {editingId ? t('createPackage.editDmc') : t('createPackage.addDmc')}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {formError ? (
            <Typography sx={{ color: DOCUMENT.oxblood, fontSize: 13 }}>{formError}</Typography>
          ) : null}
          <TextField
            label={t('createPackage.dmcCompany')}
            value={form.company_name}
            onChange={(event) => setForm((prev) => ({ ...prev, company_name: event.target.value }))}
            required
            variant="standard"
            inputProps={{ 'data-testid': 'partner-dmc-company' }}
          />
          <TextField
            label={t('createPackage.dmcContact')}
            value={form.contact_name}
            onChange={(event) => setForm((prev) => ({ ...prev, contact_name: event.target.value }))}
            variant="standard"
          />
          <TextField
            label={t('createPackage.dmcEmail')}
            value={form.email}
            onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
            variant="standard"
            inputProps={{ 'data-testid': 'partner-dmc-email' }}
          />
          <TextField
            label={t('createPackage.dmcPhone')}
            value={form.phone}
            onChange={(event) => setForm((prev) => ({ ...prev, phone: event.target.value }))}
            variant="standard"
          />
          <TextField
            label={t('createPackage.dmcNotes')}
            value={form.notes}
            onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))}
            variant="standard"
            multiline
            minRows={2}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving} sx={partnerOutlineBtnSx}>
            {t('common.cancel')}
          </Button>
          <Button onClick={saveDmc} disabled={saving} sx={partnerOxbloodBtnSx} data-testid="partner-dmc-save">
            {t('createPackage.saveDmc')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PartnerDmcField;
